import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Body, ContentCard, DetailsGrid, Tooltip } from '@indigo-ag/component-module';
import { ExposurePerCrop } from '@indigo-ag/schema';
import i18next from 'i18next';
import React from 'react';
import { date, parseNumberToLocaleInMillions, parsePercentToLocale } from 'shared';
import { text } from 'shared/utils';

const EMPTY_VALUE = '-';

interface Props {
  additionalLimit?: number | null;
  campaignName?: string | null;
  collateralTypes?: string | null;
  commitments?: number[] | null;
  currentLimit?: number | null;
  dueDate?: Date | null;
  exposurePerCrop?: ExposurePerCrop[] | null;
  exposureVsGrossRevenue?: number | null;
  grossRevenue?: number | null;
  totalExposure?: number | null;
}

export const RequestedCredit = ({
  additionalLimit,
  campaignName,
  collateralTypes,
  commitments,
  currentLimit,
  dueDate,
  exposurePerCrop,
  exposureVsGrossRevenue,
  grossRevenue,
  totalExposure
}: Props) => {
  const items = [
    {
      labelIcon: faInfoCircle,
      description: i18next.t('calculationsTooltip_currentLimit'),
      label: `${i18next.t('requestedCreditCard_currentLimit')} (M) *`,
      value: currentLimit != null ? parseNumberToLocaleInMillions(currentLimit, 3) : EMPTY_VALUE
    },
    {
      label: `${i18next.t('requestedCreditCard_additionalLimit')} (M)`,
      value: additionalLimit != null ? parseNumberToLocaleInMillions(additionalLimit, 3) : EMPTY_VALUE
    },
    {
      label: i18next.t('requestedCreditCard_collaterals'),
      value: collateralTypes ?? EMPTY_VALUE
    },
    { label: i18next.t('requestedCreditCard_campaign'), value: campaignName ?? EMPTY_VALUE },
    {
      label: i18next.t('requestedCreditCard_dueDate'),
      value: dueDate ? date.format({ date: dueDate }) : EMPTY_VALUE
    },
    {
      labelIcon: faInfoCircle,
      description: i18next.t('calculationsTooltip_totalGrossRevenue'),
      label: `${i18next.t('requestedCreditCard_grossRevenue')} (M)`,
      value: grossRevenue != null ? parseNumberToLocaleInMillions(grossRevenue, 3) : EMPTY_VALUE
    },
    {
      labelIcon: faInfoCircle,
      description: i18next.t('calculationsTooltip_totalExposure'),
      label: `${i18next.t('requestedCreditCard_totalExposure')} (M) *`,
      value: totalExposure != null ? parseNumberToLocaleInMillions(totalExposure, 3) : EMPTY_VALUE
    },
    {
      labelIcon: faInfoCircle,
      description: i18next.t('calculationsTooltip_exposureVsGrossRevenue'),
      label: `${i18next.t('requestedCreditCard_exposureVsGrossRevenue')} *`,
      value: exposureVsGrossRevenue != null ? parsePercentToLocale(exposureVsGrossRevenue, 2) : EMPTY_VALUE
    }
  ];

  if (!!exposurePerCrop?.length) {
    exposurePerCrop.forEach(exposure => {
      items.push({
        label: text.toTitleCase(`${i18next.t('requestedCreditCard_exposurePerCrop', { crop: exposure.crop })} *`),
        description: i18next.t('calculationsTooltip_exposurePerCrop', { crop: exposure.crop }),
        labelIcon: faInfoCircle,
        value: exposure.exposure ? parsePercentToLocale(exposure.exposure, 2) : EMPTY_VALUE
      });
    });
  }

  if (commitments && commitments.length > 1) {
    commitments.forEach((commitment: number, index: number) => {
      items.push({
        label: `${i18next.t('requestedCreditCard_commitment')} ${index + 1} *`,
        description: i18next.t('calculationsTooltip_commitments'),
        labelIcon: faInfoCircle,
        value: parsePercentToLocale(commitment, 2)
      });
    });
  } else if (commitments && commitments.length === 1) {
    items.push({
      label: `${i18next.t('requestedCreditCard_commitment')} *`,
      description: i18next.t('calculationsTooltip_commitments'),
      labelIcon: faInfoCircle,
      value: parsePercentToLocale(commitments[0], 2)
    });
  }

  return (
    <ContentCard
      titleText={i18next.t('requestedCreditCard_title')}
      renderFooter={() => {
        return (
          <Tooltip title={<Body>{i18next.t('requestedCreditCard_toolTip')}</Body>}>
            <FontAwesomeIcon icon={faInfoCircle} size="lg" />
          </Tooltip>
        );
      }}
    >
      <DetailsGrid items={items} columnWidth={250} />
    </ContentCard>
  );
};
